import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import '../SelectedHistory.scss';
import { parseNumberByPrecision, tableBetHistoryStore, useTranslation } from '@pascal/pascal-core';
import moment from 'moment';
import { userInfoStore } from '@pascal/pascal-core/shared/core/store';
import seatShape from '../../../assets/images/turbo-blackjack/seat-shape-full.svg';
import { useIdsGenerator } from '../../../hooks/useIdsGenerator';
import { GAME_HISTORY_BETS_COUNTS, INSURANCE_ODD } from '../../../enums/constants';
import { CARDS_TYPE } from '../../../enums/cardTypes';
import { SIDE_BET_TYPES_NAMES, SIDE_BETS_WIN_TYPES } from '../../../enums/actionTypes';
import { mathConfig, mathJS } from '../../../helpers/mathFormat';

export const SelectedRow = observer(() => {
  const { thirdStep, secondStep } = tableBetHistoryStore;
  const { data: thirdStepData } = thirdStep;
  const { data: secondStepData } = secondStep;
  const t = useTranslation();
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(null);
  const betsHistoryIds = useIdsGenerator(GAME_HISTORY_BETS_COUNTS);

  useEffect(() => {
    if (thirdStepData?.bets) {
      setCurrentHistoryIndex(+Object.keys(thirdStepData?.bets?.betsInfo)[0]);
    }
  }, [thirdStepData]);

  const memoCurrentBetsInfo = useMemo(() => {
    const currentBetsDayKey = Object.keys(secondStepData).find((item) =>
      item.includes(moment(thirdStepData.date).format('YYYY-MM-DD'))
    );
    return (
      secondStepData?.[currentBetsDayKey]?.find((item) => item.roundId === thirdStepData.roundId) ||
      []
    );
  }, [secondStepData, thirdStepData]);

  const currentRoundBetInfo = useMemo(() => {
    if (currentHistoryIndex !== null) {
      return thirdStepData?.bets?.betsInfo[currentHistoryIndex].boxBets.reduce(
        (acc, cur, index) => {
          if (!index) {
            acc.mainBetInfo = cur;
          } else if (cur.doubleBet) {
            acc.doubleBet = cur.doubleBet;
          } else if (cur.splitBet) {
            acc.splitBetInfo = cur;
          }
          acc.betsBoxCounts = Object.keys(thirdStepData.bets.betsInfo).length;
          acc.totalAmount = thirdStepData?.bets?.betsInfo[currentHistoryIndex].totalAmount;
          acc.totalWinAmount = thirdStepData?.bets?.betsInfo[currentHistoryIndex].totalWinAmount;
          return acc;
        },
        {}
      );
    }
    return null;
  }, [thirdStepData, currentHistoryIndex]);

  const totalSideBetsInfo = useMemo(() => {
    if (currentRoundBetInfo && currentRoundBetInfo.mainBetInfo.mainBet.sideBets.length) {
      return currentRoundBetInfo.mainBetInfo.mainBet.sideBets.reduce(
        (acc, cur) => {
          acc.totalSideBetAmount = +mathJS.format(acc.totalSideBetAmount + cur.amount, mathConfig);
          acc.totalSideBetWinAmount = +mathJS.format(
            acc.totalSideBetWinAmount + cur.winAmount,
            mathConfig
          );
          return acc;
        },
        {
          totalSideBetAmount: 0,
          totalSideBetWinAmount: 0,
        }
      );
    }
    return {
      totalSideBetAmount: 0,
      totalSideBetWinAmount: 0,
    };
  }, [currentRoundBetInfo]);

  return (
    <div className='tb-game-history-wrapper'>
      <div className='tb-game-history-total-window'>
        <h3 className='tb-game-history-total-head'>{t('TOTAL')}</h3>
        <div className='tb-game-history-total-row'>
          <div className='tb-game-history-total-col'>
            <h4>{t('BET')}</h4>
            <p>{memoCurrentBetsInfo.amount}</p>
          </div>
          <div className='tb-game-history-total-col'>
            <h4>{t('WIN')}</h4>
            <p>{memoCurrentBetsInfo.winAmount}</p>
          </div>
        </div>
      </div>
      <div className='tb-seat-variants'>
        {GAME_HISTORY_BETS_COUNTS.map((item, index) => (
          <div
            key={betsHistoryIds[index]}
            onClick={() => setCurrentHistoryIndex(index)}
            className={`tb-seat-variant ${currentHistoryIndex === index ? 'active' : ''} ${
              thirdStepData?.bets?.betsInfo?.[index] ? '' : 'disabled'
            }`}
          >
            <img src={seatShape} alt='' />
            <span className='tb-seat-variant-num'>{index + 1}</span>
          </div>
        ))}
      </div>
      <div className='tb-history-cards'>
        <div className='tb-history-cards-wrapper' data-title={t('DEALER')}>
          <div className='tb-history-card-list'>
            {thirdStepData &&
              thirdStepData?.dealerCards?.map(({ card, key }) => (
                <span key={key} className={`tb-history-card ${CARDS_TYPE[card]}`} />
              ))}
          </div>
          <span className='tb-history-points'>{thirdStepData.dealerTotalPoint}</span>
        </div>
      </div>
      <div className='tb-history-table'>
        <div className='tb-history-thead'>
          <div className='tb-history-th'>{t('BET_TYPE')}</div>
          <div className='tb-history-th'>{t('BET')}</div>
          <div className='tb-history-th'>{t('WIN')}</div>
        </div>
        {currentRoundBetInfo && (
          <>
            <div className='tb-history-tbody'>
              <div className='tb-history-table-row-wrapper active'>
                <div className='tb-history-table-row'>
                  <div className='tb-history-td'>
                    {t('MAIN_BET')}
                    {currentRoundBetInfo.doubleBet ? ` + ${t('DOUBLE')}` : ''}
                  </div>

                  <div className='tb-history-td'>
                    {currentRoundBetInfo.doubleBet
                      ? parseNumberByPrecision(
                          1,
                          +mathJS.format(
                            currentRoundBetInfo.mainBetInfo.mainBet.amount +
                              currentRoundBetInfo.doubleBet.amount -
                              totalSideBetsInfo.totalSideBetAmount,
                            mathConfig
                          ),
                          +userInfoStore.precision
                        )
                      : parseNumberByPrecision(
                          1,
                          +mathJS.format(
                            currentRoundBetInfo.mainBetInfo.mainBet.amount -
                              totalSideBetsInfo.totalSideBetAmount,
                            mathConfig
                          ),
                          +userInfoStore.precision
                        )}{' '}
                    {currentRoundBetInfo.mainBetInfo.mainBet.freeBetId
                      ? `(${t('FREE_CHIP')})`
                      : null}
                  </div>
                  <div className='tb-history-td'>
                    {currentRoundBetInfo.doubleBet
                      ? parseNumberByPrecision(
                          1,
                          +mathJS.format(
                            currentRoundBetInfo.mainBetInfo.mainBet.winAmount +
                              currentRoundBetInfo.doubleBet.winAmount -
                              totalSideBetsInfo.totalSideBetWinAmount,
                            mathConfig
                          ),
                          +userInfoStore.precision
                        )
                      : parseNumberByPrecision(
                          1,
                          currentRoundBetInfo.mainBetInfo.hasMainBetSurrender
                            ? +mathJS.format(
                                currentRoundBetInfo.mainBetInfo.mainBet.winAmount -
                                  totalSideBetsInfo.totalSideBetWinAmount -
                                  +mathJS.format(
                                    (currentRoundBetInfo.mainBetInfo.mainBet.amount -
                                      totalSideBetsInfo.totalSideBetAmount) /
                                      2,
                                    mathConfig
                                  ),
                                mathConfig
                              )
                            : currentRoundBetInfo.mainBetInfo.mainBet.winAmount -
                                totalSideBetsInfo.totalSideBetWinAmount,

                          +userInfoStore.precision
                        )}
                  </div>
                </div>
                {thirdStepData?.bets?.insuranceBet && (
                  <div className='tb-history-table-row'>
                    <div className='tb-history-td'>{t('INSURANCE')}</div>
                    <div className='tb-history-td'>
                      {parseNumberByPrecision(
                        1,
                        +mathJS.format(
                          (currentRoundBetInfo.mainBetInfo.mainBet.amount -
                            totalSideBetsInfo.totalSideBetAmount) /
                            2,
                          mathConfig
                        ),
                        +userInfoStore.precision
                      )}
                    </div>
                    <div className='tb-history-td'>
                      {parseNumberByPrecision(
                        1,
                        +mathJS.format(
                          thirdStepData.bets.insuranceBet.winAmount
                            ? ((currentRoundBetInfo.mainBetInfo.mainBet.amount -
                                totalSideBetsInfo.totalSideBetAmount) /
                                2) *
                                INSURANCE_ODD
                            : 0,
                          mathConfig
                        ),
                        +userInfoStore.precision
                      )}
                    </div>
                  </div>
                )}
                {currentRoundBetInfo.mainBetInfo.hasMainBetSurrender && (
                  <div className='tb-history-table-row'>
                    <div className='tb-history-td'>{t('SURRENDER')}</div>
                    <div className='tb-history-td' />
                    <div className='tb-history-td'>
                      {parseNumberByPrecision(
                        1,
                        +mathJS.format(
                          (currentRoundBetInfo.mainBetInfo.mainBet.amount -
                            totalSideBetsInfo.totalSideBetAmount) /
                            2,
                          mathConfig
                        ),
                        +userInfoStore.precision
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className='tb-history-table-row-cards'>
                <div className='tb-history-cards'>
                  <div className='tb-history-cards-wrapper'>
                    <div className='tb-history-card-list'>
                      {currentRoundBetInfo.mainBetInfo.mainBetCards?.map(({ card, key }) => (
                        <span key={key} className={`tb-history-card ${CARDS_TYPE[card]}`} />
                      ))}
                    </div>
                    <span className='tb-history-points'>
                      {currentRoundBetInfo.mainBetInfo.totalPoint}
                    </span>
                  </div>
                </div>
              </div>

              {currentRoundBetInfo.splitBetInfo && (
                <>
                  <div className='tb-history-table-row-wrapper active'>
                    <div className='tb-history-table-row'>
                      <div className='tb-history-td'>{t('SPLIT_BET')}</div>
                      <div className='tb-history-td'>
                        {currentRoundBetInfo.splitBetInfo.splitBet.amount}{' '}
                        {currentRoundBetInfo.mainBetInfo.mainBet.freeBetId
                          ? `(${t('FREE_CHIP')})`
                          : null}
                      </div>
                      <div className='tb-history-td'>
                        {currentRoundBetInfo.splitBetInfo.splitBet.winAmount}
                      </div>
                    </div>
                  </div>
                  <div className='tb-history-table-row-cards'>
                    <div className='tb-history-cards'>
                      <div className='tb-history-cards-wrapper'>
                        <div className='tb-history-card-list'>
                          {currentRoundBetInfo.splitBetInfo.splitBetCards?.map(({ card, key }) => (
                            <span key={key} className={`tb-history-card ${CARDS_TYPE[card]}`} />
                          ))}
                        </div>
                        <span className='tb-history-points'>
                          {currentRoundBetInfo.splitBetInfo.totalPoint}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {currentRoundBetInfo?.mainBetInfo?.mainBet?.sideBets
                ? [...currentRoundBetInfo.mainBetInfo.mainBet.sideBets]
                    ?.sort((a, b) => b.type - a.type)
                    .map(({ type, winAmount, amount, winType }) => (
                      <div key={type} className='tb-history-table-row'>
                        <div className='tb-history-td'>
                          {t(SIDE_BET_TYPES_NAMES[type])}{' '}
                          {SIDE_BETS_WIN_TYPES[winType] ? t(SIDE_BETS_WIN_TYPES[winType]) : ''}
                        </div>
                        <div className='tb-history-td'>
                          {currentRoundBetInfo.mainBetInfo.mainBet.freeBetId ? t('FREE_BET') : null}{' '}
                          {parseNumberByPrecision(1, amount, +userInfoStore.precision)}
                        </div>
                        <div className='tb-history-td'>{winAmount}</div>
                      </div>
                    ))
                : null}
            </div>
            <div className='tb-history-tfooter'>
              <div className='tb-history-table-row'>
                <div className='tb-history-td'>{t('TOTAL')}</div>
                <div className='tb-history-td'>
                  {thirdStepData?.bets?.insuranceBet
                    ? parseNumberByPrecision(
                        1,
                        +mathJS.format(
                          currentRoundBetInfo.totalAmount +
                            (currentRoundBetInfo.mainBetInfo.mainBet.amount -
                              totalSideBetsInfo.totalSideBetAmount) /
                              2,
                          mathConfig
                        ),
                        +userInfoStore.precision
                      )
                    : parseNumberByPrecision(
                        1,
                        +mathJS.format(currentRoundBetInfo.totalAmount, mathConfig),
                        +userInfoStore.precision
                      )}
                </div>
                <div className='tb-history-td'>
                  {thirdStepData?.bets?.insuranceBet?.winAmount
                    ? parseNumberByPrecision(
                        1,
                        +mathJS.format(
                          currentRoundBetInfo.totalWinAmount +
                            ((currentRoundBetInfo.mainBetInfo.mainBet.amount -
                              totalSideBetsInfo.totalSideBetAmount) /
                              2) *
                              INSURANCE_ODD,
                          mathConfig
                        ),
                        +userInfoStore.precision
                      )
                    : parseNumberByPrecision(
                        1,
                        +mathJS.format(currentRoundBetInfo.totalWinAmount, mathConfig),
                        +userInfoStore.precision
                      )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
});
